import { useState, useEffect } from 'react';
import { API } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { Box, Plus, GripVertical, Settings, Trash2, Cog } from 'lucide-react';

export default function WorkshopPage() {
  const [ilots, setIlots] = useState([]);
  const [machines, setMachines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMachine, setSelectedMachine] = useState(null);
  const [machineDialogOpen, setMachineDialogOpen] = useState(false);
  const [draggedMachine, setDraggedMachine] = useState(null);
  const [editMachineData, setEditMachineData] = useState({ name: '', type: '', ilot_id: '' });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [ilotsRes, machinesRes] = await Promise.all([
        fetch(`${API}/ilots`, { credentials: 'include' }),
        fetch(`${API}/machines`, { credentials: 'include' })
      ]);
      
      if (ilotsRes.ok && machinesRes.ok) {
        const ilotsData = await ilotsRes.json();
        const machinesData = await machinesRes.json();
        setIlots(ilotsData);
        setMachines(machinesData);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Erreur de chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const handleDragStart = (e, machine) => {
    setDraggedMachine(machine);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, targetIlotId) => {
    e.preventDefault();
    if (!draggedMachine || draggedMachine.ilot_id === targetIlotId) {
      setDraggedMachine(null);
      return;
    }

    try {
      const response = await fetch(`${API}/machines/${draggedMachine.machine_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ ilot_id: targetIlotId })
      });

      if (response.ok) {
        toast.success('Machine déplacée');
        fetchData();
      }
    } catch (error) {
      toast.error('Erreur lors du déplacement');
    }
    setDraggedMachine(null);
  };

  const handleEditMachine = (machine) => {
    setSelectedMachine(machine);
    setEditMachineData({
      name: machine.name,
      type: machine.type,
      ilot_id: machine.ilot_id
    });
    setMachineDialogOpen(true);
  };

  const handleUpdateMachine = async () => {
    if (!selectedMachine) return;

    try {
      const response = await fetch(`${API}/machines/${selectedMachine.machine_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(editMachineData)
      });

      if (response.ok) {
        toast.success('Machine mise à jour');
        setMachineDialogOpen(false);
        fetchData();
      }
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleDeleteMachine = async () => {
    if (!selectedMachine) return;

    try {
      const response = await fetch(`${API}/machines/${selectedMachine.machine_id}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Machine supprimée');
        setMachineDialogOpen(false);
        fetchData();
      }
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const getMachinesForIlot = (ilotId) => {
    return machines.filter(m => m.ilot_id === ilotId);
  };

  const machineTypes = {
    'cnc': { label: 'CNC', color: 'bg-blue-500' },
    'tour': { label: 'Tour', color: 'bg-green-500' },
    'fraiseuse': { label: 'Fraiseuse', color: 'bg-orange-500' },
    'perceuse': { label: 'Perceuse', color: 'bg-purple-500' },
    'rectifieuse': { label: 'Rectifieuse', color: 'bg-pink-500' },
    'autre': { label: 'Autre', color: 'bg-gray-500' }
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="workshop-loading">
        <div className="flex justify-between items-center">
          <Skeleton className="h-10 w-48" />
          <Skeleton className="h-10 w-32" />
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3].map(i => (
            <Skeleton key={i} className="h-64" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="workshop-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-heading font-bold text-3xl tracking-tight uppercase">
            Vue Atelier
          </h1>
          <p className="text-muted-foreground mt-1 font-mono text-sm uppercase tracking-wider">
            Glissez-déposez les machines entre les îlots
          </p>
        </div>
      </div>

      {ilots.length === 0 ? (
        <Card className="border-2 rounded-sm border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <Box className="w-16 h-16 text-muted-foreground mb-4" strokeWidth={1} />
            <h3 className="font-heading font-semibold text-xl uppercase tracking-tight mb-2">
              Aucun îlot configuré
            </h3>
            <p className="text-muted-foreground text-center mb-4 max-w-md">
              Commencez par créer des îlots de production dans la section "Gestion Îlots".
            </p>
            <Button asChild className="btn-industrial rounded-sm" data-testid="create-ilot-btn">
              <a href="/ilots">
                <Plus className="w-4 h-4 mr-2" />
                Créer un îlot
              </a>
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-6">
          {ilots.map((ilot) => {
            const ilotMachines = getMachinesForIlot(ilot.ilot_id);
            const isDropTarget = draggedMachine && draggedMachine.ilot_id !== ilot.ilot_id;
            
            return (
              <Card
                key={ilot.ilot_id}
                className={`border-2 rounded-sm transition-all ${
                  isDropTarget ? 'border-primary border-dashed bg-primary/5' : ''
                }`}
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, ilot.ilot_id)}
                data-testid={`ilot-card-${ilot.ilot_id}`}
              >
                <CardHeader className="pb-4 border-b-2 border-border">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-sm bg-primary/10 flex items-center justify-center">
                        <Box className="w-5 h-5 text-primary" strokeWidth={1.5} />
                      </div>
                      <div>
                        <CardTitle className="font-heading font-semibold text-lg uppercase tracking-tight">
                          {ilot.name}
                        </CardTitle>
                        <p className="font-mono text-xs text-muted-foreground">
                          {ilotMachines.length} machine{ilotMachines.length !== 1 ? 's' : ''}
                        </p>
                      </div>
                    </div>
                    <Badge variant="outline" className="font-mono text-xs">
                      {ilot.width}px
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="pt-4">
                  <ScrollArea className="h-48">
                    {ilotMachines.length === 0 ? (
                      <div className="flex flex-col items-center justify-center h-40 text-center">
                        <Cog className="w-10 h-10 text-muted-foreground/50 mb-2" strokeWidth={1} />
                        <p className="text-sm text-muted-foreground font-mono uppercase tracking-wider">
                          Aucune machine
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          Glissez une machine ici
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {ilotMachines.map((machine) => {
                          const typeInfo = machineTypes[machine.type] || machineTypes.autre;
                          return (
                            <div
                              key={machine.machine_id}
                              draggable
                              onDragStart={(e) => handleDragStart(e, machine)}
                              className={`machine-card p-3 cursor-grab active:cursor-grabbing ${
                                draggedMachine?.machine_id === machine.machine_id ? 'opacity-50' : ''
                              }`}
                              data-testid={`machine-card-${machine.machine_id}`}
                            >
                              <div className="flex items-center gap-3">
                                <GripVertical className="w-4 h-4 text-muted-foreground drag-handle" />
                                <div className={`w-2 h-8 rounded-full ${typeInfo.color}`} />
                                <div className="flex-1 min-w-0">
                                  <p className="font-medium text-sm truncate">{machine.name}</p>
                                  <p className="font-mono text-xs text-muted-foreground uppercase">
                                    {typeInfo.label}
                                  </p>
                                </div>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-8 w-8 p-0"
                                  onClick={() => handleEditMachine(machine)}
                                  data-testid={`edit-machine-${machine.machine_id}`}
                                >
                                  <Settings className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </ScrollArea>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Edit Machine Dialog */}
      <Dialog open={machineDialogOpen} onOpenChange={setMachineDialogOpen}>
        <DialogContent className="rounded-sm border-2">
          <DialogHeader>
            <DialogTitle className="font-heading font-semibold text-xl uppercase tracking-tight">
              Modifier la machine
            </DialogTitle>
            <DialogDescription className="font-mono text-xs uppercase tracking-wider">
              Modifiez les informations de la machine
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="industrial-label">Nom</Label>
              <Input
                value={editMachineData.name}
                onChange={(e) => setEditMachineData({ ...editMachineData, name: e.target.value })}
                className="rounded-sm border-2"
                data-testid="edit-machine-name"
              />
            </div>
            <div className="space-y-2">
              <Label className="industrial-label">Type</Label>
              <Select
                value={editMachineData.type}
                onValueChange={(value) => setEditMachineData({ ...editMachineData, type: value })}
              >
                <SelectTrigger className="rounded-sm border-2" data-testid="edit-machine-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(machineTypes).map(([key, value]) => (
                    <SelectItem key={key} value={key}>{value.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="industrial-label">Îlot</Label>
              <Select
                value={editMachineData.ilot_id}
                onValueChange={(value) => setEditMachineData({ ...editMachineData, ilot_id: value })}
              >
                <SelectTrigger className="rounded-sm border-2" data-testid="edit-machine-ilot">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ilots.map((ilot) => (
                    <SelectItem key={ilot.ilot_id} value={ilot.ilot_id}>{ilot.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="destructive"
              onClick={handleDeleteMachine}
              className="rounded-sm"
              data-testid="delete-machine-btn"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Supprimer
            </Button>
            <Button
              onClick={handleUpdateMachine}
              className="btn-industrial rounded-sm"
              data-testid="update-machine-btn"
            >
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
